import {Alert, Box, Button, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../system/layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import {AudienceGlobalConfig} from "crqlar-react-common/dist/models/audience/audienceGlobalConfig.model";
import {AudienceGlobalConfigService} from "crqlar-react-common/dist/services/audience/audienceGlobalConfig.service";
import NewAudienceFilters from "./NewAudienceFilters";
import {
    useAllAudienceProfileFilterOptions,
    useAudienceProfileFilterOptions
} from "crqlar-react-common/dist/utils/hooks/audience/useAudience";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import {useNavigate} from "react-router-dom";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

const AudienceGlobalConfigDetails = () => {

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()
    const audienceFilters = useAudienceProfileFilterOptions() || []
    // Full option set so a stored rule on a hidden field still shows its name.
    const allAudienceFilters = useAllAudienceProfileFilterOptions() || []

    const configData = useEntity<AudienceGlobalConfig>({
        get: AudienceGlobalConfigService.get,
        update: AudienceGlobalConfigService.update,
        create: AudienceGlobalConfigService.update,
    })

    const config = configData.item
    const setConfig = configData.setItem

    const readOnlyUser = useReadOnly()

    //endregion Sate

    //region Handlers

    const updateField = (field: keyof AudienceGlobalConfig, value: any) => {
        setConfig({...config, [field]: value})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={t('Audience global configuration')}
            data={configData}
            back={() => navigate('/system')}
        />
    )

    // The same rules block the audience dialogs use, at full width: a consent filter is an audience
    // filter by another name, and one builder means one behaviour to learn.
    const filterSection = (
        <Stack gap={2}>
            {
                config.newsletterOptionFilter ?
                    <Alert
                        variant={'outlined'}
                        severity={'warning'}
                        sx={{width: '100%', py: 0, lineHeight: 1, alignItems: 'center'}}
                    >
                        {t('Please make sure to add at least one filter')}
                    </Alert>
                    :
                    <Alert
                        variant={'outlined'}
                        severity={'info'}
                        sx={{width: '100%', py: 0, lineHeight: 1, alignItems: 'center'}}
                    >
                        {t('Please specify which profiles have marketing consent')}
                    </Alert>
            }

            <NewAudienceFilters
                filter={config.newsletterOptionFilter || {}}
                setFilter={filter => updateField('newsletterOptionFilter', filter.filterGroups?.length ? filter : undefined)}
                filterOptions={audienceFilters}
                labelOptions={allAudienceFilters}
                readOnly={readOnlyUser}
            />
        </Stack>
    )

    const emptyStateSection = (
        <EmptyState
            icon={<HowToRegOutlinedIcon/>}
            title={t('No consent settings configured')}
            description={t('Create a consent filter to define which profiles are included in marketing audiences. Until then, the default newsletter consent filter is used.')}
        >
            <Button
                variant={'contained'}
                onClick={() => configData.startCreate()}
                disabled={readOnlyUser}
            >
                {t('Create')}
            </Button>
        </EmptyState>
    )

    //endregion UI

    if (configData.loading) {
        return (
            <Box sx={{width: '100%', pb: 8}}>
                {headerSection}
                <DetailsLoading/>
            </Box>
        )
    }

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '800px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {configData.isMissing ? emptyStateSection : (
                    <>
                        {filterSection}

                        {config.updateDate && (
                            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                                {t('Last updated')}: {config.updateDate.toLocaleString()}
                            </Typography>
                        )}
                    </>
                )}
            </Stack>
        </Box>
    );
}

export default AudienceGlobalConfigDetails;
